import React from 'react';
import { Plus, MessageSquare, CheckCircle, XCircle, Clock, DollarSign, Filter, Eye, EyeOff, Edit, Trash2 } from 'lucide-react';

const logStatusColor = (status) => (
  status === 'DELIVERED' ? 'bg-green-100 text-green-800' :
  status === 'SENT' ? 'bg-blue-100 text-blue-800' :
  status === 'FAILED' ? 'bg-red-100 text-red-800' :
  status === 'CANCELLED' ? 'bg-gray-100 text-gray-800' :
  'bg-yellow-100 text-yellow-800'
);

const SmsManagementTab = ({ sms }) => {
  const {
    smsTemplates, loadingSmsTemplates, smsLogs, loadingSmsLogs, smsStats,
    setShowAddSmsTemplateModal, selectedSmsFilter, setSelectedSmsFilter, smsLogPagination,
    selectedSmsLog, showSmsLogDetails, closeSmsLogDetails, viewSmsLog,
    smsLogFilters, setSmsLogFilters,
    fetchSmsLogs, handleSmsCardClick,
    handleDeleteSmsTemplate, handleToggleSmsTemplateStatus, handlePreviewSmsTemplate, handleEditSmsTemplate,
  } = sms;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">SMS Management</h2>
        <button onClick={() => setShowAddSmsTemplateModal(true)} className="bg-[#d44100] text-white px-4 py-2 rounded-lg hover:bg-[#b83700] flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* SMS Statistics - Interactive Cards */}
      {smsStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div
            onClick={() => {
              setSelectedSmsFilter(null);
              document.getElementById('sms-templates-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Templates</p>
                <p className="text-2xl font-bold text-blue-600">{smsStats.templates?.total || 0}</p>
                <p className="text-xs text-gray-500">{smsStats.templates?.active || 0} active</p>
                <p className="text-xs text-blue-600 mt-1 font-medium">Click to view templates</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div
            onClick={() => handleSmsCardClick('sent')}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${selectedSmsFilter === 'sent' ? 'ring-2 ring-green-500' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">SMS Sent</p>
                <p className="text-2xl font-bold text-green-600">{smsStats.sms?.total || 0}</p>
                <p className="text-xs text-gray-500">{smsStats.sms?.successRate || 0}% success rate</p>
                <p className="text-xs text-green-600 mt-1 font-medium">Click to view ({smsStats.sms?.total || 0} messages)</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div
            onClick={() => handleSmsCardClick('failed')}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${selectedSmsFilter === 'failed' ? 'ring-2 ring-red-500' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Failed SMS</p>
                <p className="text-2xl font-bold text-red-600">{smsStats.sms?.failed || 0}</p>
                <p className="text-xs text-gray-500">{smsStats.sms?.pending || 0} pending</p>
                <p className="text-xs text-red-600 mt-1 font-medium">Click to view ({smsStats.sms?.failed || 0} messages)</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div
            onClick={() => handleSmsCardClick('pending')}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${selectedSmsFilter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending SMS</p>
                <p className="text-2xl font-bold text-yellow-600">{smsStats.sms?.pending || 0}</p>
                <p className="text-xs text-gray-500">Awaiting delivery</p>
                <p className="text-xs text-yellow-600 mt-1 font-medium">Click to view ({smsStats.sms?.pending || 0} messages)</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div
            onClick={() => { setSelectedSmsFilter(null); handleSmsCardClick('all'); }}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${selectedSmsFilter === null || selectedSmsFilter === 'all' ? 'ring-2 ring-purple-500' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Cost</p>
                <p className="text-2xl font-bold text-purple-600">KSh {smsStats.cost?.total?.toLocaleString() || 0}</p>
                <p className="text-xs text-gray-500">SMS expenses</p>
                <p className="text-xs text-purple-600 mt-1 font-medium">Click to view all</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Filter Indicator */}
      {selectedSmsFilter && (
        <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <Filter className="h-5 w-5 text-blue-600 mr-2" />
            <p className="text-sm font-medium text-blue-800">
              Showing {selectedSmsFilter === 'sent' ? 'Sent & Delivered' : selectedSmsFilter.toUpperCase()} SMS messages
            </p>
          </div>
          <button onClick={() => { setSelectedSmsFilter(null); fetchSmsLogs(1, null); }} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            Clear Filter
          </button>
        </div>
      )}

      {/* SMS Templates Table */}
      <div id="sms-templates-section" className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">SMS Templates</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingSmsTemplates ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                    </div>
                  </td>
                </tr>
              ) : smsTemplates.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No SMS templates found</td>
                </tr>
              ) : (
                smsTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{template.name}</div>
                        <div className="text-sm text-gray-500">{template.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {template.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{template.category || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${template.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {template.isDefault && (
                        <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Default</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{template.usageCount || 0} times</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button onClick={() => handlePreviewSmsTemplate(template.id)} className="text-blue-600 hover:text-blue-900" title="Preview">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleEditSmsTemplate(template)} className="text-green-600 hover:text-green-900" title="Edit">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleSmsTemplateStatus(template.id, template.isActive)}
                          className={`${template.isActive ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                          title={template.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {template.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button onClick={() => handleDeleteSmsTemplate(template.id)} className="text-red-600 hover:text-red-900" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SMS Logs with Enhanced Filtering */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            SMS Logs {selectedSmsFilter && `(${smsLogPagination.total} ${selectedSmsFilter === 'sent' ? 'sent/delivered' : selectedSmsFilter} messages)`}
          </h3>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              placeholder="Search by recipient or message..."
              value={smsLogFilters.search}
              onChange={(e) => setSmsLogFilters({ ...smsLogFilters, search: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent text-sm"
            />
            <select
              value={smsLogFilters.status}
              onChange={(e) => {
                setSmsLogFilters({ ...smsLogFilters, status: e.target.value });
                setSelectedSmsFilter(e.target.value || null);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent text-sm"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="SENT">Sent</option>
              <option value="DELIVERED">Delivered</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message Preview</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingSmsLogs ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                    </div>
                  </td>
                </tr>
              ) : smsLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>No SMS logs found</p>
                    {selectedSmsFilter && (
                      <button onClick={() => { setSelectedSmsFilter(null); fetchSmsLogs(1, null); }} className="mt-2 text-sm text-orange-600 hover:text-orange-700 hover:underline transition-colors">
                        Clear filter to see all messages
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                smsLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => viewSmsLog(log)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{log.recipient}</div>
                      {log.order?.orderNumber && <div className="text-xs text-gray-500">Order: {log.order.orderNumber}</div>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={log.message}>{log.message}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.template?.name || <span className="text-gray-400 italic">Direct SMS</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${logStatusColor(log.status)}`}>{log.status}</span>
                      {log.errorMessage && (
                        <div className="text-xs text-red-600 mt-1 truncate max-w-xs" title={log.errorMessage}>{log.errorMessage}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.sentAt ? (
                        <div>
                          <div>{new Date(log.sentAt).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">{new Date(log.sentAt).toLocaleTimeString()}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">Not sent</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.cost ? (
                        <div>
                          <div className="font-medium">KSh {log.cost.toFixed(2)}</div>
                          {log.provider && <div className="text-xs text-gray-500">{log.provider}</div>}
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button onClick={(e) => { e.stopPropagation(); viewSmsLog(log); }} className="text-[#d44100] hover:text-[#b83700]" title="View Details">
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {smsLogPagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((smsLogPagination.page - 1) * smsLogPagination.limit) + 1} to {Math.min(smsLogPagination.page * smsLogPagination.limit, smsLogPagination.total)} of {smsLogPagination.total} messages
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => fetchSmsLogs(smsLogPagination.page - 1, selectedSmsFilter)}
                disabled={smsLogPagination.page === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">Page {smsLogPagination.page} of {smsLogPagination.pages}</span>
              <button
                onClick={() => fetchSmsLogs(smsLogPagination.page + 1, selectedSmsFilter)}
                disabled={smsLogPagination.page >= smsLogPagination.pages}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SMS Log Details Modal */}
      {showSmsLogDetails && selectedSmsLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">SMS Message Details</h3>
                <button onClick={closeSmsLogDetails} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-600 mb-2">Recipient</h4>
                  <p className="text-lg font-semibold text-gray-900">{selectedSmsLog.recipient}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-600 mb-2">Message</h4>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedSmsLog.message}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Status</h4>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${logStatusColor(selectedSmsLog.status)}`}>{selectedSmsLog.status}</span>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Template</h4>
                    <p className="text-gray-900">{selectedSmsLog.template?.name || 'Direct SMS'}</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Sent At</h4>
                    <p className="text-gray-900">{selectedSmsLog.sentAt ? new Date(selectedSmsLog.sentAt).toLocaleString() : 'Not sent'}</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Cost</h4>
                    <p className="text-gray-900">{selectedSmsLog.cost ? `KSh ${selectedSmsLog.cost.toFixed(2)}` : 'N/A'}</p>
                  </div>
                </div>

                {selectedSmsLog.errorMessage && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Error Message</h4>
                    <p className="text-red-900">{selectedSmsLog.errorMessage}</p>
                  </div>
                )}

                {selectedSmsLog.order && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Related Order</h4>
                    <p className="text-blue-900">Order #{selectedSmsLog.order.orderNumber}</p>
                  </div>
                )}

                {selectedSmsLog.provider && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Provider Details</h4>
                    <p className="text-gray-900">Provider: {selectedSmsLog.provider}</p>
                    {selectedSmsLog.providerId && <p className="text-gray-900 text-sm mt-1">Provider ID: {selectedSmsLog.providerId}</p>}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button onClick={closeSmsLogDetails} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsManagementTab;
